import React, { useState } from 'react';
import { Landmark, Copy, Check } from 'lucide-react';

const group4 = s => String(s || '').replace(/(\d{4})(?=\d)/g, '$1 ');

/** Per-bank selectable boxes: each bank shows its own card/sheba/account numbers.
 * Used in wallet charge modal + checkout manual payment (same look everywhere). */
export function ManualPayBoxes({ cards = [], selected = 0, onSelect }) {
  const [copied, setCopied] = useState(null);
  const copy = (key, text) => {
    try {
      const done = () => { setCopied(key); setTimeout(() => setCopied(null), 1500); };
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(String(text)).then(done).catch(done);
      else {
        const ta = document.createElement('textarea');
        ta.value = String(text); document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); } catch {}
        document.body.removeChild(ta); done();
      }
    } catch {}
  };
  const numRow = (boxKey, label, value) => value ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: '0.74rem', color: 'var(--muted-fg)', fontWeight: 700, flexShrink: 0, minWidth: 74 }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0, fontWeight: 800, fontSize: '0.88rem', color: 'var(--fg)', letterSpacing: 0.5, overflowWrap: 'anywhere', wordBreak: 'break-all', lineHeight: 1.9 }} dir="ltr">{value}</span>
      <button type="button" onClick={e => { e.stopPropagation(); copy(boxKey + label, String(value).replace(/ /g, '')); }} title="کپی"
        style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', color: copied === boxKey + label ? 'var(--accent)' : 'var(--muted-fg)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {copied === boxKey + label ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  ) : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {cards.map((c, i) => {
        const active = i === selected;
        return (
          <div key={i} onClick={() => onSelect && onSelect(i)} role="radio" aria-checked={active} tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect && onSelect(i); } }}
            style={{ border: active ? '2px solid var(--primary)' : '1px solid var(--border)', background: active ? 'var(--muted)' : 'var(--card)', borderRadius: 14, padding: '12px 14px', cursor: onSelect ? 'pointer' : 'default', transition: 'border-color 0.15s, background 0.15s' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 4 }}>
              <span style={{ width: 38, height: 38, borderRadius: 11, background: active ? 'var(--primary)' : 'var(--muted)', color: active ? 'var(--on-primary)' : 'var(--fg)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Landmark size={18} />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ display: 'block', fontSize: '0.86rem', color: 'var(--fg)' }}>{c.bank || 'حساب فروشگاه'}</strong>
                <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--muted-fg)' }}>{c.holder ? 'به‌نام ' + c.holder : ''}</span>
              </span>
              <span style={{ width: 22, height: 22, borderRadius: '50%', border: active ? '6px solid var(--primary)' : '2px solid var(--border)', background: 'var(--card)', flexShrink: 0, boxSizing: 'border-box' }} />
            </div>
            {active && (
              <div style={{ marginTop: 6 }}>
                {numRow(i, 'شماره کارت', group4(c.number))}
                {numRow(i, 'شماره شبا', c.sheba)}
                {numRow(i, 'شماره حساب', c.account)}
              </div>
            )}
            {!active && (
              <div style={{ fontSize: '0.7rem', color: 'var(--muted-fg)', paddingTop: 4 }}>برای مشاهده شماره‌ها انتخاب کنید</div>
            )}
            {c.desc && <div style={{ fontSize: '0.76rem', color: 'var(--muted-fg)', paddingTop: 6, lineHeight: 1.9 }}>{c.desc}</div>}
          </div>
        );
      })}
    </div>
  );
}
export default function ShopAccountView({ card }) {
  if (!card) return null;
  const row = (label, value, ltr) => value ? (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: '0.84rem' }}>
      <span style={{ color: 'var(--muted-fg)', fontWeight: 600, flexShrink: 0 }}>{label}</span>
      <span style={{ fontWeight: 800, letterSpacing: ltr ? 1 : 0, color: 'var(--fg)' }} dir={ltr ? 'ltr' : 'rtl'}>{value}</span>
    </div>
  ) : null;
  return (
    <div style={{ background: 'var(--muted)', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 14px 10px', marginBottom: 10 }}>
      {row('شماره کارت', group4(card.number), true)}
      {row('شماره شبا', card.sheba, true)}
      {row('شماره حساب', card.account, true)}
      <div style={{ fontSize: '0.78rem', color: 'var(--muted-fg)', paddingTop: 7 }}>
        {[card.bank, card.holder ? 'به‌نام ' + card.holder : ''].filter(Boolean).join(' — ') || 'حساب فروشگاه'}
      </div>
      {card.desc && (
        <div style={{ fontSize: '0.78rem', color: 'var(--fg)', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', marginTop: 8, lineHeight: 1.9 }}>
          {card.desc}
        </div>
      )}
    </div>
  );
}
